import OpenAI from "openai";

// 从环境变量获取配置
const API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY;

// 创建 OpenAI 客户端实例
const openai = new OpenAI({
  baseURL: 'https://api.deepseek.com/v1',
  apiKey: API_KEY,
  timeout: 30000,
  dangerouslyAllowBrowser: true, // 允许在浏览器中使用
  defaultHeaders: {
    'Content-Type': 'application/json',
  }
});

interface TranslationResponse {
  errorCode?: string;
  translation?: string[];
  title?: string;
  description?: string;
  tags?: string[];
  comparison?: {
    original?: {
      material?: string;
      craftsmanship?: string;
    };
    replica?: {
      material?: string;
      craftsmanship?: string;
    };
  };
}

function generateTranslationPrompt(text: string, from: string, to: string): string {
  return `你是一个专业的翻译助手。请将以下文本从${from}翻译成${to}。
只需要返回翻译后的文本，不需要任何解释或额外的内容。
保持专业性，确保翻译准确、自然、符合目标语言的表达习惯。

原文：${text}`;
}

export async function translateText(text: string, from: string = 'zh-CN', to: string = 'en'): Promise<string> {
  const requestStartTime = Date.now();
  
  try {
    console.log('=== 开始翻译文本 ===');
    console.log('输入文本:', text);
    console.log('源语言:', from);
    console.log('目标语言:', to);
    console.log('API配置:', {
      baseURL: openai.baseURL,
      hasApiKey: !!openai.apiKey,
      timeout: openai.timeout
    });

    const prompt = generateTranslationPrompt(text, from, to);

    try {
      const completion = await openai.chat.completions.create({
        model: "deepseek-chat",
        messages: [
          {
            role: "system",
            content: "你是一个专业的翻译助手，只返回翻译后的文本，不需要任何解释或额外内容。"
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 1000
      });

      const requestDuration = Date.now() - requestStartTime;
      console.log('请求耗时:', requestDuration, 'ms');

      if (!completion.choices?.[0]?.message?.content) {
        console.error('API返回数据不完整:', completion);
        throw new Error('API返回数据格式错误');
      }

      const translatedText = completion.choices[0].message.content.trim();
      console.log('翻译结果:', translatedText);

      return translatedText;
    } catch (error: unknown) {
      const errorTime = Date.now() - requestStartTime;
      if (error instanceof Error) {
        console.error('请求失败:', {
          errorType: error.name,
          errorMessage: error.message,
          timeElapsed: errorTime
        });
      }
      throw error;
    }
  } catch (error) {
    console.error('=== 翻译过程失败 ===');
    console.error('总耗时:', Date.now() - requestStartTime, 'ms');
    console.error('错误详情:', {
      type: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
}

export async function translateProduct(values: any, targetLang: string): Promise<TranslationResponse> {
  const requestStartTime = Date.now();

  try {
    console.log('=== 开始批量翻译商品信息 ===');
    
    // 收集所有需要翻译的文本
    const textsToTranslate = [
      values.title || '',
      values.description || '',
      ...(values.tags ? values.tags.split(',').map((tag: string) => tag.trim()) : []),
      values.comparison?.original?.material || '',
      values.comparison?.original?.craftsmanship || '',
      values.comparison?.replica?.material || '',
      values.comparison?.replica?.craftsmanship || ''
    ].filter(text => text.length > 0);

    console.log('需要翻译的文本列表:', textsToTranslate);
    console.log('API配置:', {
      baseURL: openai.baseURL,
      hasApiKey: !!openai.apiKey,
      timeout: openai.timeout
    });

    try {
      const completion = await openai.chat.completions.create({
        model: "deepseek-chat",
        messages: [
          {
            role: "system",
            content: "你是一个专业的翻译助手。请将输入的文本数组从中文翻译成英文，并以 JSON 格式返回，格式为：{\"translated_text\": [翻译后的文本数组]}。保持原数组顺序，确保翻译准确、自然。"
          },
          {
            role: "user",
            content: `请将以下文本数组翻译成英文：${JSON.stringify(textsToTranslate)}`
          }
        ],
        temperature: 0.3,
        max_tokens: 1000,
        response_format: { type: "json_object" }
      });

      const requestDuration = Date.now() - requestStartTime;
      console.log('请求耗时:', requestDuration, 'ms');

      if (!completion.choices?.[0]?.message?.content) {
        console.error('API返回数据不完整:', completion);
        throw new Error('API返回数据格式错误');
      }

      const translatedContent = completion.choices[0].message.content.trim();
      console.log('API返回的翻译内容:', translatedContent);

      // 解析翻译结果
      let translations: string[];
      try {
        const parsedContent = JSON.parse(translatedContent);
        // 检查是否有 translated_text 字段
        if (Array.isArray(parsedContent.translated_text)) {
          translations = parsedContent.translated_text;
        } else if (Array.isArray(parsedContent)) {
          translations = parsedContent;
        } else if (parsedContent.translations && Array.isArray(parsedContent.translations)) {
          translations = parsedContent.translations;
        } else {
          throw new Error('翻译结果格式错误');
        }
        
        if (!Array.isArray(translations)) {
          throw new Error('翻译结果格式错误');
        }
      } catch (e) {
        console.error('JSON解析失败:', e);
        console.error('问题内容:', translatedContent);
        throw new Error('返回数据格式错误，无法解析为JSON');
      }

      // 确保翻译结果数量匹配
      if (translations.length !== textsToTranslate.length) {
        console.error('翻译结果数量不匹配:', {
          original: textsToTranslate.length,
          translated: translations.length
        });
        throw new Error('翻译结果数量与原文不匹配');
      }

      // 解构翻译结果
      const [
        translatedTitle,
        translatedDescription,
        ...restTranslations
      ] = translations;

      // 处理标签翻译
      const originalTagCount = values.tags ? values.tags.split(',').length : 0;
      const translatedTags = restTranslations.slice(0, originalTagCount);

      // 处理比较信息翻译
      const [
        originalMaterial,
        originalCraftsmanship,
        replicaMaterial,
        replicaCraftsmanship
      ] = restTranslations.slice(originalTagCount);

      const translationResult = {
        title: translatedTitle,
        description: translatedDescription,
        tags: translatedTags,
        comparison: {
          original: {
            material: originalMaterial,
            craftsmanship: originalCraftsmanship
          },
          replica: {
            material: replicaMaterial,
            craftsmanship: replicaCraftsmanship
          }
        }
      };

      console.log('=== 翻译完成 ===');
      console.log('总耗时:', Date.now() - requestStartTime, 'ms');
      console.log('最终翻译结果:', translationResult);

      return translationResult;

    } catch (error: unknown) {
      const errorTime = Date.now() - requestStartTime;
      if (error instanceof Error) {
        console.error('请求失败:', {
          errorType: error.name,
          errorMessage: error.message,
          timeElapsed: errorTime
        });
      }
      throw error;
    }
  } catch (error) {
    console.error('=== 批量翻译过程失败 ===');
    console.error('总耗时:', Date.now() - requestStartTime, 'ms');
    console.error('错误详情:', {
      type: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
} 